import {
  closestCenter,
  defaultDropAnimationSideEffects,
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  DropAnimation,
  KeyboardSensor,
  PointerSensor,
  UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { snapCenterToCursor } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Button } from 'components/atoms';
import EmptyPlaceholder from 'components/atoms/empty/EmptyPlaceholder';
import TrackRoleCard from 'components/molecules/card/track-role/TrackRoleCard';
import AlbumTabs from 'components/molecules/track-albums/AlbumTabs';
import { useFormikContext } from 'formik';
import { IFormikContextType } from 'hooks/useFormikField';
import i18next from 'i18next';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiPlus } from 'react-icons/pi';
import { useGetTrackLightList } from 'services/tracks/light';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { measuring } from 'utils/dnd/dndUtilities';
import { array, number, object, string } from 'yup';

export const trackInitialValue = {
  name: '',
  id: '',
  isrc: '',
  mp3Link: '',
  mp3PreviewLink: '',
  wavLink: '',
  flacLink: '',
};

const createUid = () => {
  const c = globalThis.crypto as Crypto | undefined;
  if (c && typeof (c as any).randomUUID === 'function') {
    return (c as any).randomUUID() as string;
  }

  return `uid_${Date.now()}_${Math.random().toString(16).slice(2)}`;
};

type ITrackFormBase = typeof trackInitialValue;
// ✅ dodajemy opcjonalnie albumPlacement, żeby TS się nie czepiał
type ITrackForm = ITrackFormBase & { _uid: string; albumPlacement?: number };

type Album = {
  albumNumber: number;
  tracks: ITrackForm[];
};

const tracksValidation = object().shape({
  id: string().required(() => i18next.t('formik-error.required')),
  name: string(),
});

export const albumValidation = object().shape({
  tracks: array()
    .of(tracksValidation)
    .min(1, () => i18next.t('formik-error.at-least-one-track-required'))
    .required(() => i18next.t('formik-error.required')),
});

// ✅ nie tworzy nowych referencji jeśli wszystkie mają _uid
const ensureUids = (albums: Album[]) =>
  albums.every((a) => a.tracks.every((t) => !!t._uid))
    ? albums
    : albums.map((a) => ({
        ...a,
        tracks: a.tracks.map((t) => (t._uid ? t : { ...t, _uid: createUid() })),
      }));

// ✅ helper: nadaje albumPlacement (1..n) wg bieżącej kolejności
const normalizeAlbums = (albums: Album[]): Album[] =>
  albums.map((a) => ({
    ...a,
    tracks: a.tracks.map((t, idx) => ({
      ...t,
      albumPlacement: idx + 1,
    })),
  }));

const dropAnimationConfig: DropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: { active: { opacity: '0.85' } },
  }),
};

const TrackRoleTab = <Form extends Record<string, Album[]>>({
  propertyName = 'tracks',
}: {
  propertyName?: keyof Form | string;
}) => {
  const key = propertyName as string;
  const { values, status, setFieldValue }: IFormikContextType<Form> = useFormikContext<Form>();
  const { t } = useTranslation();
  useGetTrackLightList();

  const albumsFormik = useMemo(
    () => ensureUids((values[key] as unknown as Album[]) || []),
    [values, key],
  );

  const [selectedAlbumNumber, setSelectedAlbumNumber] = useState<number>(
    albumsFormik[0]?.albumNumber ?? 1,
  );

  const [shadowAlbums, setShadowAlbums] = useState<Album[] | null>(null);
  const albumsToRender = shadowAlbums ?? albumsFormik;

  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [activeAlbumNumber, setActiveAlbumNumber] = useState<number | null>(null);
  const [hoveredAlbum, setHoveredAlbum] = useState<number | null>(null);
  const hoverTimerRef = useRef<number | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  /* ---------- Utils ---------- */

  const findMetaByUid = useCallback((albums: Album[], uid: string) => {
    for (const a of albums) {
      const index = a.tracks.findIndex((t) => t._uid === uid);
      if (index !== -1) return { albumNumber: a.albumNumber, index };
    }
    return null;
  }, []);

  const moveBetweenAlbums = (
    albums: Album[],
    fromAlbum: number,
    fromIndex: number,
    toAlbum: number,
    toIndex: number,
  ): Album[] => {
    const updated = albums.map((a) => ({ ...a, tracks: [...a.tracks] }));
    const srcIdx = updated.findIndex((a) => a.albumNumber === fromAlbum);
    const dstIdx = updated.findIndex((a) => a.albumNumber === toAlbum);
    if (srcIdx === -1 || dstIdx === -1) return albums;
    const [moving] = updated[srcIdx].tracks.splice(fromIndex, 1);
    updated[dstIdx].tracks.splice(toIndex, 0, moving);
    return updated;
  };

  const reorderInAlbum = (
    albums: Album[],
    albumNumber: number,
    from: number,
    to: number,
  ): Album[] => {
    const updated = albums.map((a) => ({ ...a, tracks: [...a.tracks] }));
    const idx = updated.findIndex((a) => a.albumNumber === albumNumber);
    if (idx === -1) return albums;
    updated[idx].tracks = arrayMove(updated[idx].tracks, from, to);
    return updated;
  };

  const clearHoverTimer = () => {
    if (hoverTimerRef.current) {
      window.clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  };

  const resetDrag = () => {
    clearHoverTimer();
    setActiveId(null);
    setActiveAlbumNumber(null);
    setHoveredAlbum(null);
    setShadowAlbums(null);
    document.body.style.cursor = '';
  };

  /* ---------- Drag Handlers ---------- */
  const handleDragStart = ({ active }: DragStartEvent) => {
    setActiveId(active.id);
    const meta = findMetaByUid(albumsToRender, String(active.id));
    if (meta) setActiveAlbumNumber(meta.albumNumber);
    document.body.style.cursor = 'grabbing';
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { over, active } = event;
    if (!over) {
      setHoveredAlbum(null);
      clearHoverTimer();
      return;
    }

    const overId = String(over.id);

    if (overId.startsWith('album-drop-')) {
      const targetAlbum = parseInt(overId.replace('album-drop-', ''), 10);
      if (!activeId) return;
      if (hoveredAlbum === targetAlbum) return;

      setHoveredAlbum(targetAlbum);
      clearHoverTimer();

      hoverTimerRef.current = window.setTimeout(() => {
        const source = shadowAlbums ?? albumsFormik;
        const meta = findMetaByUid(source, String(active.id));
        if (!meta || meta.albumNumber === targetAlbum) return;

        const injected = moveBetweenAlbums(source, meta.albumNumber, meta.index, targetAlbum, 0);

        setShadowAlbums(injected);
        setTimeout(() => {
          setSelectedAlbumNumber(targetAlbum);
          setActiveAlbumNumber(targetAlbum);
        }, 50);
      }, 250) as unknown as number;
    }
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    const uid = String(active.id);
    const sourceAlbums = shadowAlbums ?? albumsFormik;
    if (!over) {
      resetDrag();
      return;
    }

    const overId = String(over.id);
    const activeMeta = findMetaByUid(sourceAlbums, uid);
    if (!activeMeta) return resetDrag();

    let finalAlbums = sourceAlbums;

    if (overId.startsWith('album-drop-')) {
      const targetAlbum = parseInt(overId.replace('album-drop-', ''), 10);
      finalAlbums =
        activeMeta.albumNumber === targetAlbum
          ? sourceAlbums
          : moveBetweenAlbums(
              sourceAlbums,
              activeMeta.albumNumber,
              activeMeta.index,
              targetAlbum,
              0,
            );
      setSelectedAlbumNumber(targetAlbum);
    } else {
      const overMeta = findMetaByUid(sourceAlbums, overId);
      if (!overMeta) return resetDrag();
      if (activeMeta.albumNumber === overMeta.albumNumber) {
        finalAlbums = reorderInAlbum(
          sourceAlbums,
          activeMeta.albumNumber,
          activeMeta.index,
          overMeta.index,
        );
      } else {
        finalAlbums = moveBetweenAlbums(
          sourceAlbums,
          activeMeta.albumNumber,
          activeMeta.index,
          overMeta.albumNumber,
          overMeta.index,
        );
        setSelectedAlbumNumber(overMeta.albumNumber);
      }
    }

    // ✅ przed zapisem do Formika nadajmy albumPlacement wg aktualnej kolejności
    const normalized = normalizeAlbums(finalAlbums);

    if (JSON.stringify(normalized) !== JSON.stringify(values[key])) {
      setFieldValue(key, normalized);
    }

    resetDrag();
  };

  const handleDragCancel = () => resetDrag();

  /* ---------- Actions ---------- */
  const onAlbumSelect = (num: number) => {
    if (num === selectedAlbumNumber) return;
    clearHoverTimer();
    setHoveredAlbum(null);
    setSelectedAlbumNumber(num);
  };

  const onAddTrack = () => {
    const base = shadowAlbums ?? albumsFormik;
    const idx = base.findIndex((a) => a.albumNumber === selectedAlbumNumber);
    if (idx === -1) return;
    const updated = base.map((a) => ({ ...a, tracks: [...a.tracks] }));
    updated[idx].tracks.unshift({
      ...trackInitialValue,
      _uid: createUid(),
    });
    setFieldValue(key, normalizeAlbums(updated));
  };

  const onAddAlbum = () => {
    const base = shadowAlbums ?? albumsFormik;
    const next = (base[base.length - 1]?.albumNumber ?? 0) + 1;
    const updated = [
      ...base,
      {
        albumNumber: next,
        tracks: [{ ...trackInitialValue, _uid: createUid() }],
      },
    ];
    // nowy album ma 1 track → też nadajemy placement
    setFieldValue(key, normalizeAlbums(updated));
    setSelectedAlbumNumber(next);
  };

  const onRemoveAlbum = (albumNumber: number) => {
    const base = shadowAlbums ?? albumsFormik;
    if (base.length === 1) return;

    const updated = base
      .filter((a) => a.albumNumber !== albumNumber)
      .map((a, idx) => ({
        ...a,
        albumNumber: idx + 1,
      }));

    setFieldValue(key, normalizeAlbums(updated));
    const next = albumNumber > 1 ? albumNumber - 1 : (updated[0]?.albumNumber ?? 1);
    setSelectedAlbumNumber(next);
  };

  /* ---------- Render ---------- */
  const currentAlbum = albumsToRender.find((a) => a.albumNumber === selectedAlbumNumber);

  return (
    <div className="flex flex-col w-full gap-4">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        measuring={measuring}
        onDragStart={handleDragStart}
        modifiers={[snapCenterToCursor]}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <AlbumTabs
          albums={albumsFormik}
          selectedAlbumNumber={selectedAlbumNumber}
          onAlbumSelect={onAlbumSelect}
          hoveredAlbum={hoveredAlbum}
          onAddAlbum={onAddAlbum}
          onRemoveAlbum={onRemoveAlbum}
          displayMode={status?.displayMode}
        />

        {!status?.displayMode && currentAlbum && (
          <Button fullWidth variant={BUTTON_VARIANT.white} onClick={onAddTrack}>
            <Button.Icon icon={<PiPlus />} />
            <Button.Text text={t('button.add-track')} />
          </Button>
        )}

        {currentAlbum && (
          <SortableContext
            items={currentAlbum.tracks.map((t) => t._uid)}
            strategy={verticalListSortingStrategy}
          >
            {currentAlbum.tracks.length ? (
              currentAlbum.tracks.map((track, index) => (
                <TrackRoleCard
                  key={track._uid}
                  index={index}
                  track={track}
                  sortableKey={track._uid}
                  name={`${key}[${selectedAlbumNumber - 1}].tracks[${index}]`}
                  displayMode={status?.displayMode}
                  onRemove={() => {
                    const updated = albumsFormik.map((a) => ({
                      ...a,
                      tracks:
                        a.albumNumber === currentAlbum.albumNumber
                          ? a.tracks.filter((t) => t._uid !== track._uid)
                          : a.tracks,
                    }));
                    setFieldValue(key, normalizeAlbums(updated)); // ✅ placement po usunięciu tracka
                  }}
                />
              ))
            ) : (
              <EmptyPlaceholder />
            )}
          </SortableContext>
        )}
        {!albumsFormik.length && status?.displayMode && <EmptyPlaceholder />}
        <DragOverlay dropAnimation={dropAnimationConfig}>
          {activeId && activeAlbumNumber && (
            <div style={{ opacity: 0.9, width: 'max-content' }}>
              <TrackRoleCard
                track={
                  albumsToRender
                    .find((a) => a.albumNumber === activeAlbumNumber)
                    ?.tracks.find((t) => t._uid === activeId) || {
                    ...trackInitialValue,
                    _uid: String(activeId),
                  }
                }
                index={0}
                sortableKey={String(activeId)}
                displayMode={status?.displayMode}
                name={`tracks[${activeAlbumNumber}].tracks`}
              />
            </div>
          )}
        </DragOverlay>
      </DndContext>
    </div>
  );
};

export default TrackRoleTab;
